import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import Text from '@cartwhl/ui/base/TextNew';
import GoogleIcon from '@cartwhl/ui/icons/GoogleIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';

export type SignupMethod = 'email' | 'google';

interface SignupMethodToggleProps {
  value: SignupMethod;
  onChange: (value: SignupMethod) => void;
}

const OPTIONS: { value: SignupMethod; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'google', label: 'Google' },
];

function renderOptionIcon(option: SignupMethod, isActive: boolean) {
  if (option === 'google') {
    return <GoogleIcon className="size-5" colored={isActive} />;
  }

  return <MailIcon className="size-5" />;
}

export default function SignupMethodToggle({
  value,
  onChange,
}: SignupMethodToggleProps) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [highlight, setHighlight] = useState({ left: 0, width: 0 });
  const hasMeasured = useRef(false);

  const updateHighlight = useCallback(() => {
    const activeIndex = OPTIONS.findIndex((option) => option.value === value);
    const activeButton =
      buttonRefs.current[activeIndex >= 0 ? activeIndex : 0] ?? null;

    if (activeButton) {
      setHighlight({
        left: activeButton.offsetLeft,
        width: activeButton.offsetWidth,
      });
      hasMeasured.current = true;
    }
  }, [value]);

  useLayoutEffect(() => {
    updateHighlight();
  }, [updateHighlight]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateHighlight();
    });

    const container = containerRef.current;
    if (container) {
      observer.observe(container);
    }

    buttonRefs.current.forEach((button) => {
      if (button) {
        observer.observe(button);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [updateHighlight]);

  return (
    <div className="mb-6">
      <Text as="p" token="14" className="text-primary my-4">
        Continue with
      </Text>
      <div
        ref={containerRef}
        role="radiogroup"
        aria-label="Sign up method"
        className="bg-gray-30 border-gray-30 relative flex w-full flex-row flex-nowrap items-center rounded-xl border-4"
      >
        <div
          className={`absolute top-0 z-0 h-full rounded-lg bg-white ${
            hasMeasured.current ? 'transition-all duration-200' : ''
          }`}
          style={{
            left: highlight.left,
            width: highlight.width,
          }}
        />
        {OPTIONS.map((option, index) => {
          const isActive = value === option.value;

          return (
            <button
              key={option.value}
              ref={(button) => {
                buttonRefs.current[index] = button;
              }}
              type="button"
              className="z-10 flex flex-1 items-center justify-center gap-2 px-3 py-2"
              onClick={() => {
                if (option.value !== value) {
                  onChange(option.value);
                }
              }}
            >
              <span
                className={`transition-colors ${
                  isActive ? 'text-primary' : 'text-secondary'
                }`}
              >
                {renderOptionIcon(option.value, isActive)}
              </span>
              <Text
                as="span"
                token="16/medium"
                className={`whitespace-nowrap transition-colors ${
                  isActive ? 'text-primary' : 'text-secondary'
                }`}
              >
                {option.label}
              </Text>
            </button>
          );
        })}
      </div>
    </div>
  );
}
